import { defineStore } from 'pinia';
import { computed, ref, set, del } from 'vue';
import { isMatch } from 'lodash-es';
import type { MigrationTask, MigrationTaskProgressUpdate } from '@schema';
import {
  mapMigrationTask,
  type MappedMigrationTask
} from '@/common/store/pinia/migrationTasks/migrationTasksMapper';
import { useMigrationsStore } from '@/common/store/pinia/migrations';
import { useReplicationsStore } from '@/common/store/pinia/replications';
import { useDeploymentsStore } from '@/common/store/pinia/deployments';
import { isNotificationForCurrentProject } from '@/common/utils/notifications/isNotificationForCurrentProject';

export type MigrationTaskParentType = 'deployment' | 'replication';

interface StoredMigrationTask extends MigrationTask {
  parent_id: string;
  parent_type: MigrationTaskParentType;
  migration_id: string;
}

interface ProgressUpdatePayload {
  id?: string;
  task_id?: string;
  index?: number;
  message?: string;
  created_at?: string;
  progress?: number;
  migration_id?: string;
  execution_id?: string;
  deployment_id?: string | null;
  project_id?: string;
}

export const useMigrationTasksStore = defineStore('migrationTasks', () => {
  const migrationsStore = useMigrationsStore();
  const replicationsStore = useReplicationsStore();
  const deploymentsStore = useDeploymentsStore();

  const tasksMap = ref<Record<string, StoredMigrationTask>>({});

  const getTaskById = (id: string): StoredMigrationTask | undefined => tasksMap.value[id];

  const setTasksForParent = (
    parent: { id?: string; migration_id?: string | null; tasks?: MigrationTask[] | null },
    parentType: MigrationTaskParentType,
    fallbackMigrationId = ''
  ) => {
    const parentId = parent.id;
    if (!parentId) return;

    const migrationId = parent.migration_id ?? fallbackMigrationId;
    const incoming = parent.tasks ?? [];

    const incomingIds = new Set(
      incoming
        .map(incomingTask => incomingTask.id)
        .filter((incomingTaskId): incomingTaskId is string => Boolean(incomingTaskId))
    );

    Object.entries(tasksMap.value).forEach(([taskId, storedTask]) => {
      if (storedTask.parent_id === parentId && !incomingIds.has(taskId)) {
        del(tasksMap.value, taskId);
      }
    });

    incoming.forEach((task) => {
      const { id } = task;
      if (!id) return;

      const existing = tasksMap.value[id];
      const next: StoredMigrationTask = {
        ...(existing ?? {}),
        ...task,
        parent_id: parentId,
        parent_type: parentType,
        migration_id: migrationId
      };
      if (existing && isMatch(existing, next)) return;
      set(tasksMap.value, id, next);
    });
  };

  const removeTasksForParent = (parentId: string) => {
    if (!parentId) return;

    Object.entries(tasksMap.value).forEach(([taskId, storedTask]) => {
      if (storedTask.parent_id === parentId) {
        del(tasksMap.value, taskId);
      }
    });
  };

  const appendProgressUpdate = (
    taskId: string,
    update: MigrationTaskProgressUpdate
  ) => {
    const existing = tasksMap.value[taskId];
    if (!existing) return;

    const updates = existing.progress_updates ?? [];

    const existingUpdateIndex = update.id
      ? updates.findIndex(existingUpdate => existingUpdate.id === update.id)
      : -1;

    if (existingUpdateIndex >= 0 && isMatch(updates[existingUpdateIndex], update)) return;

    const next = existingUpdateIndex >= 0
      ? updates.map((existingUpdate, updateIndex) => (
        updateIndex === existingUpdateIndex ? { ...existingUpdate, ...update } : existingUpdate
      ))
      : [...updates, update];

    set(tasksMap.value, taskId, {
      ...existing,
      progress_updates: next,
      updated_at: update.created_at ?? existing.updated_at
    });
  };

  const getMappedTasksByParent = computed(() => (parentId: string): MappedMigrationTask[] => {
    if (!parentId) return [];

    return Object.values(tasksMap.value)
      .filter(task => task.parent_id === parentId)
      .sort((firstTask, secondTask) => (firstTask.index ?? 0) - (secondTask.index ?? 0))
      .map(mapMigrationTask);
  });

  const fetchRelatedEntities = (object: MigrationTask & ProgressUpdatePayload) => {
    if (!isNotificationForCurrentProject(object.project_id)) return;

    const migrationId = object.migration_id;
    if (!migrationId) return;

    migrationsStore.fetchLastItem(migrationId);

    const deploymentId = object.deployment_id;
    if (deploymentId) {
      deploymentsStore.fetchItemLatest(migrationId, deploymentId);
      return;
    }

    const executionId = object.execution_id;
    if (executionId) {
      replicationsStore.fetchItemLatest(migrationId, executionId);
    }
  };

  const onCompute = (data: SocketEvent<MigrationTask & ProgressUpdatePayload>) => {
    if (!data?.object) return;

    if (data.type === 'migration_tasks') {
      fetchRelatedEntities(data.object as MigrationTask & ProgressUpdatePayload);
      return;
    }

    if (data.type === 'migration_task_progress_updates') {
      const progressUpdate = data.object as ProgressUpdatePayload;
      if (!progressUpdate.task_id) return;

      appendProgressUpdate(progressUpdate.task_id, {
        id: progressUpdate.id,
        index: progressUpdate.index,
        message: progressUpdate.message,
        created_at: progressUpdate.created_at,
        progress: progressUpdate.progress
      });
    }
  };

  return {
    tasksMap,
    getTaskById,
    setTasksForParent,
    removeTasksForParent,
    getMappedTasksByParent,
    onCompute
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useMigrationTasksStore;
